import { useState, useCallback, useRef, useEffect } from 'react';
import { generateImportPrompt } from '../prompts/import';
import { parseAIResponse } from '../utils/ai';
import { useCoreStore } from '../stores/coreStore';
import { calculateNodePosition } from '../utils/canvas';
import { supabase } from '../lib/supabase';

const IMPORT_COLOR = '#1e66f5';
const MAX_SIDE_PX = 1500;
const JPEG_QUALITY = 0.85;
const MAX_BYTES_PER_IMAGE = 4 * 1024 * 1024;
const CAPTURE_BUCKET = 'captures';
const CAPTURE_POLL_MS = 2500;

// ─── image helpers ────────────────────────────────────────────────────────────

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      const scale = Math.min(1, MAX_SIDE_PX / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compressione immagine fallita')); return; }
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Immagine non leggibile')); };
    img.src = url;
  });

const filesToImages = async (files) => {
  const images = await Promise.all(files.map(async (file) => {
    const base64 = await compressImage(file);
    if (base64.length > MAX_BYTES_PER_IMAGE) {
      throw new Error(`Un'immagine è troppo grande anche dopo la compressione (${Math.round(base64.length / 1024)} KB). Prova a fotografare una sezione più piccola.`);
    }
    return { base64, mimeType: 'image/jpeg' };
  }));
  return images;
};

// ─── map builder (shared between file upload and capture) ─────────────────────

const buildMapFromOps = (ops, createNewMap, mapName, setNodes, setConnections, setMessages, pageLabel) => {
  createNewMap(mapName || 'Mappa da libro');

  const nodesToAdd = [];
  const connectionsToAdd = [];
  const tempIdMap = {};

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (op.action !== 'add' || !op.title || !['problem', 'solution'].includes(op.type)) continue;
    if (op.type === 'problem' && nodesToAdd.some(n => n.type === 'problem')) continue;

    let parentNode = null;
    if (op.parentId && tempIdMap[op.parentId]) {
      parentNode = nodesToAdd.find(n => n.id === tempIdMap[op.parentId]) || null;
    }
    if (!parentNode && op.type === 'solution') {
      parentNode = nodesToAdd.find(n => n.type === 'problem') || null;
    }

    const pos = calculateNodePosition(op.type, nodesToAdd, connectionsToAdd, parentNode);
    const newId = `${Date.now()}-${i}`;
    if (op.tempId) tempIdMap[op.tempId] = newId;

    nodesToAdd.push({
      id: newId,
      type: op.type,
      title: op.title.trim(),
      summary: (op.summary || '').trim(),
      description: '',
      ...pos,
      banned: false,
      color: IMPORT_COLOR,
      source: 'import',
      _hidden: false,
      _isGhost: false
    });

    if (parentNode) {
      connectionsToAdd.push({ id: `${parentNode.id}-${newId}`, from: parentNode.id, to: newId });
    }
  }

  setNodes(nodesToAdd);
  setConnections(connectionsToAdd);
  setMessages([{
    role: 'assistant',
    content: `Estratti ${nodesToAdd.length} concetti da ${pageLabel}. Chiedimi di espandere qualsiasi nodo o approfondire un argomento.`
  }]);

  return nodesToAdd.length;
};

// ─── hook ─────────────────────────────────────────────────────────────────────

export const useImportMap = (provider, createNewMap) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captureId, setCaptureId] = useState(null);
  const [captureStatus, setCaptureStatus] = useState('idle'); // 'idle' | 'waiting' | 'processing'
  const pollRef = useRef(null);
  const { setNodes, setConnections, setMessages } = useCoreStore();

  // Stop polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── file upload ──────────────────────────────────────────────────────────────

  const importFromImage = useCallback(async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    if (!provider?.chatWithImage) {
      setError('Il provider attuale non supporta vision. Usa Claude, GPT-4o o Gemini.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const images = await filesToImages(fileList);
      const systemPrompt = generateImportPrompt();
      const userPrompt = fileList.length > 1
        ? `Analizza queste ${fileList.length} pagine consecutive e genera un'unica mappa concettuale coerente.`
        : 'Analizza questa pagina e genera la mappa concettuale.';

      const rawResponse = await provider.chatWithImage(images, userPrompt, systemPrompt);
      const parsed = parseAIResponse(rawResponse);
      if (!parsed?.ops?.length) {
        setError("Nessun concetto estratto. Prova con una foto più nitida o un'inquadratura migliore.");
        return false;
      }

      const pageLabel = fileList.length > 1 ? `${fileList.length} pagine` : 'una pagina';
      buildMapFromOps(parsed.ops, createNewMap, parsed.mapName, setNodes, setConnections, setMessages, pageLabel);
      return true;
    } catch (err) {
      setError(err.message || "Errore durante l'elaborazione dell'immagine.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [provider, createNewMap, setNodes, setConnections, setMessages]);

  // ── mobile capture ───────────────────────────────────────────────────────────

  const startCapture = useCallback(() => {
    const id = crypto.randomUUID();
    setCaptureId(id);
    setCaptureStatus('waiting');
    setError(null);

    pollRef.current = setInterval(async () => {
      try {
        const { data, error: dlError } = await supabase.storage
          .from(CAPTURE_BUCKET)
          .download(`${id}.jpg`);

        if (dlError || !data) return; // non ancora disponibile

        clearInterval(pollRef.current);
        pollRef.current = null;
        setCaptureStatus('processing');

        // Converti blob in File e processa
        const file = new File([data], `${id}.jpg`, { type: 'image/jpeg' });
        if (!provider?.chatWithImage) {
          setError('Il provider attuale non supporta vision. Usa Claude, GPT-4o o Gemini.');
          setCaptureStatus('idle');
          return;
        }

        try {
          const base64 = await compressImage(file);
          const systemPrompt = generateImportPrompt();
          const rawResponse = await provider.chatWithImage(
            [{ base64, mimeType: 'image/jpeg' }],
            'Analizza questa pagina fotografata e genera la mappa concettuale.',
            systemPrompt
          );
          const parsed = parseAIResponse(rawResponse);
          if (!parsed?.ops?.length) {
            setError("Nessun concetto estratto dalla foto. Riprova con una luce migliore.");
            setCaptureStatus('idle');
            return;
          }
          buildMapFromOps(parsed.ops, createNewMap, parsed.mapName, setNodes, setConnections, setMessages, 'una pagina fotografata');
          setCaptureStatus('done');
        } catch (err) {
          setError(err.message || "Errore durante l'elaborazione.");
          setCaptureStatus('idle');
        } finally {
          // Cleanup file temporaneo
          supabase.storage.from(CAPTURE_BUCKET).remove([`${id}.jpg`]).catch(() => {});
        }
      } catch {
        // poll silenzioso
      }
    }, CAPTURE_POLL_MS);
  }, [provider, createNewMap, setNodes, setConnections, setMessages]);

  const stopCapture = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setCaptureId(null);
    setCaptureStatus('idle');
    setError(null);
  }, []);

  const captureUrl = captureId
    ? `https://learnatlas.app/capture?id=${captureId}&sb=${encodeURIComponent(import.meta.env.VITE_SUPABASE_URL)}&key=${encodeURIComponent(import.meta.env.VITE_SUPABASE_ANON_KEY)}`
    : null;

  const clearError = useCallback(() => setError(null), []);

  return { importFromImage, isLoading, error, clearError, captureId, captureStatus, captureUrl, startCapture, stopCapture };
};
